import {
  Directive,
  Input,
  OnDestroy,
  OnInit,
  ElementRef,
  Renderer2,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { SharedDataService } from '../../shared.data.service';
import { HelperService } from '../../../../services/helper.service';
import { PermissionService } from '../../../../services/permissions/permission.service';

/**
 * Enables/disables an element based on a permission slug.
 *
 * Re-evaluates whenever permissions change, so an admin revoking a permission
 * disables the control in every open tab immediately — no logout, no reload.
 * Used in ~137 templates; making the directive reactive covers all of them.
 */
@Directive({
    selector: '[appPermission]',
    standalone: false
})
export class PermissionsDirective implements OnInit, OnDestroy {
  permissions: any;
  @Input('appPermission') requiredPermission!: string;

  private permissionSub?: Subscription;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private sharedDataService: SharedDataService,
    private helperService: HelperService,
    private permissionService: PermissionService
  ) {}

  ngOnInit() {
    // permissions$ is a BehaviorSubject, so this fires synchronously with the
    // current value first — same behaviour as the old ngOnInit-only evaluation —
    // and again on every subsequent change.
    this.permissionSub = this.permissionService.permissions$.subscribe(() =>
      this.updateElement()
    );
  }

  ngOnDestroy() {
    this.permissionSub?.unsubscribe();
  }

  private updateElement() {
    if (this.requiredPermission !== 'DONOTAPPLY') {
      this.permissions = this.sharedDataService.getPermissions();
      const nativeElement = this.el.nativeElement;
      const nodeName = nativeElement.nodeName;
      const isSuperAdmin = this.sharedDataService.getIsCurrentUserSuperAdmin()
      if (!isSuperAdmin){
        // Was a raw permissions.includes(), which disagreed with the normalized
        // matching every other permission check uses (casing, _ vs -).
        if (this.hasAnyRequiredPermission()) {
          this.enableElement(nativeElement,nodeName)
        } else {
          this.disabledElement(nativeElement,nodeName);
        }
      }

      if(this.requiredPermission == 'disable'){
        this.disabledElement(nativeElement,nodeName);
      }

    }
  }

  /**
   * Scoped permission families (View/Edit/Delete x All/Own/Assigned/Office) have
   * no single unscoped slug in the catalog, so a template passes the whole family
   * as a comma-separated list and the control unlocks if the role holds ANY scope.
   * The backend still narrows the action to the records that scope actually covers.
   * A single slug -- how the other ~137 usages call this -- takes the same path
   * and behaves exactly as before.
   */
  private hasAnyRequiredPermission(): boolean {
    return (this.requiredPermission || '')
      .split(',')
      .map((slug) => slug.trim())
      .filter((slug) => slug.length > 0)
      .some((slug) => this.helperService.isPermissionAvailable(slug));
  }

  /**
   * True while the disabled state on the host was applied by THIS directive.
   *
   * Many hosts combine the directive with their own state binding, e.g.
   *   appPermission="PROPOSAL-CONVERT-TO-JOB" [disabled]="isLocked()"
   * Unconditionally clearing `disabled` on a granted permission wiped that
   * binding out: the directive re-runs on every permissions$ emission, which
   * lands after change detection has written the binding, and Angular only
   * writes again when the bound value itself changes -- so the button stayed
   * clickable on a locked record for the rest of the page's life. Only ever
   * undo a disable we are responsible for; anything the host set is the host's.
   */
  private disabledByDirective = false;

  enableElement(nativeElement: any, nodeName: any){
    if (!this.disabledByDirective) {
      return;
    }
    this.disabledByDirective = false;
    this.renderer.removeClass(nativeElement, 'permission-disabled');
    if (this.isInteractiveElement(nodeName)) {
      this.renderer.removeAttribute(nativeElement, 'disabled');
    }
  }
  disabledElement(nativeElement: any, nodeName: any){
    this.disabledByDirective = true;
    this.renderer.addClass(nativeElement, 'permission-disabled');
    if (this.isInteractiveElement(nodeName)) {
      this.renderer.setAttribute(nativeElement, 'disabled', 'true');
    }
  }

  private isInteractiveElement(nodeName: string): boolean {
    return (
      nodeName === 'BUTTON' ||
      nodeName === 'INPUT' ||
      nodeName === 'SELECT' ||
      nodeName === 'TEXTAREA' ||
      nodeName === 'MAT-SLIDE-TOGGLE' ||
      nodeName === 'MAT-SELECT'
    );
  }
}
